import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Users, DollarSign, CalendarCheck } from 'lucide-react';
import { MinistriesService, type Evento } from '../services/ministries/ministries.service';
import { EventoInscripcionesDrawer } from '../components/ministries/EventoInscripcionesDrawer';

interface EventoConMinisterio extends Evento {
  ministerioNombres?: string;
}

const estadoColors: Record<string, string> = {
  programado: 'bg-blue-100 text-blue-700',
  en_curso: 'bg-green-100 text-green-700',
  finalizado: 'bg-gray-100 text-gray-700',
  cancelado: 'bg-red-100 text-red-700',
  aplazado: 'bg-yellow-100 text-yellow-700',
};

const estadoLabels: Record<string, string> = {
  programado: 'Programado',
  en_curso: 'En curso',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  aplazado: 'Aplazado',
};

export const DashboardEventosPage: React.FC = () => {
  const [eventos, setEventos] = useState<EventoConMinisterio[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [selectedEvento, setSelectedEvento] = useState<EventoConMinisterio | null>(null);
  const [inscripcionesEventoId, setInscripcionesEventoId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchEventos = async () => {
      setLoading(true);
      try {
        const data = await MinistriesService.findAllEventos();
        const mapped = (data || []).map((ev) => ({
          ...ev,
          ministerioNombres: (ev.ministerios || []).map((m: any) => m.nombre).join(', ') || 'Iglesia',
        }));
        mapped.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
        if (!cancelled) setEventos(mapped);
      } catch (err) {
        if (!cancelled) setEventos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEventos();
    return () => {
      cancelled = true;
    };
  }, []);

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  const esEventoPasado = (fecha: string) => fecha < todayStr;
  const esEventoProximo = (fecha: string) => fecha >= todayStr;

  const eventosPorFecha = useMemo(() => {
    const map: Record<string, EventoConMinisterio[]> = {};
    eventos.forEach((ev) => {
      const key = ev.fecha.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [eventos]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startDayOfWeek = firstDayOfMonth.getDay();

    const days: Array<{ date: number; fullDate: string; isCurrentMonth: boolean }> = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({ date: d, fullDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: d, fullDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      days.push({ date: d, fullDate: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    return days;
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return value;
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (value?: number) => {
    if (value == null) return null;
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header fijo */}
      <div className="px-6 pt-6 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-xs font-medium text-gray-900">
            {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-gray-600">Cargando eventos...</span>
          </div>
        )}

        {!loading && eventos.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500">No hay eventos registrados</div>
        )}

        {!loading && eventos.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full min-h-0">
            {/* Calendario fijo */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
              <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 shrink-0">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((d) => (
                  <div key={d} className="py-2 text-center text-xs font-medium text-gray-600">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 flex-1 auto-rows-fr overflow-hidden">
                {getCalendarDays(calendarMonth).map((day, idx) => {
                  const dayEvents = eventosPorFecha[day.fullDate] || [];
                  return (
                    <div
                      key={idx}
                      className={`border-b border-r border-gray-100 p-2 flex flex-col gap-1 ${
                        day.isCurrentMonth ? 'bg-white' : 'bg-gray-50/50'
                      } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
                    >
                      <span
                        className={`self-start text-xs font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                          day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {day.date}
                      </span>
                      <div className="flex flex-col gap-1 overflow-hidden">
                        {dayEvents.slice(0, 3).map((ev) => {
                          const pasado = esEventoPasado(ev.fecha);
                          const estadoClass = estadoColors[ev.estado] || 'bg-gray-100 text-gray-500';
                          return (
                            <button
                              key={ev.id}
                              onClick={() => setSelectedEvento(ev)}
                              className={`w-full text-left text-xs px-2 py-1 rounded truncate transition-colors ${
                                pasado ? 'bg-gray-200 text-gray-600 line-through decoration-gray-400' : estadoClass
                              } hover:opacity-80`}
                              title={ev.titulo}
                            >
                              {ev.titulo}
                            </button>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <span className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} más</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resumen mensual con scroll */}
            <div className="lg:col-span-1 bg-gray-50 border border-gray-200 rounded-xl flex flex-col overflow-hidden h-full min-h-0">
              <div className="px-4 py-3 border-b border-gray-200 bg-white shrink-0">
                <h4 className="font-medium text-gray-900">Eventos de {monthNames[calendarMonth.getMonth()]}</h4>
                <p className="text-xs text-gray-500">
                  {(() => {
                    const year = calendarMonth.getFullYear();
                    const month = calendarMonth.getMonth();
                    const eventosDelMes = eventos.filter((ev) => {
                      const [y, m] = ev.fecha.slice(0, 10).split('-').map(Number);
                      return y === year && m - 1 === month;
                    });
                    return `${eventosDelMes.length} evento${eventosDelMes.length !== 1 ? 's' : ''}`;
                  })()}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
                {(() => {
                  const year = calendarMonth.getFullYear();
                  const month = calendarMonth.getMonth();
                  const eventosDelMes = eventos
                    .filter((ev) => {
                      const [y, m] = ev.fecha.slice(0, 10).split('-').map(Number);
                      return y === year && m - 1 === month;
                    })
                    .sort((a, b) => a.fecha.localeCompare(b.fecha));

                  if (eventosDelMes.length === 0) {
                    return <div className="text-center text-gray-500 py-6 text-xs">No hay eventos este mes</div>;
                  }

                  return eventosDelMes.map((ev) => {
                    const pasado = esEventoPasado(ev.fecha);
                    return (
                      <button
                        key={ev.id}
                        onClick={() => setSelectedEvento(ev)}
                        className={`w-full text-left border rounded-lg p-3 transition-colors ${
                          pasado ? 'bg-gray-100 border-gray-200 hover:bg-gray-200' : 'bg-white border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-medium text-xs ${pasado ? 'text-gray-500' : 'text-gray-900'}`}>
                            {ev.titulo}
                          </p>
                          <span
                            className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${estadoColors[ev.estado] || 'text-gray-600 bg-gray-200'}`}
                          >
                            {estadoLabels[ev.estado] || ev.estado}
                          </span>
                        </div>
                        <p className={`text-xs ${pasado ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(ev.fecha)}</p>
                        {ev.lugar && (
                          <p className={`text-xs ${pasado ? 'text-gray-400' : 'text-gray-500'}`}>📍 {ev.lugar}</p>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
      {selectedEvento && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedEvento(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-medium text-gray-900">{selectedEvento.titulo}</h3>
              <button
                onClick={() => setSelectedEvento(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Estado */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${estadoColors[selectedEvento.estado] || 'bg-gray-100 text-gray-600'}`}>
                  {estadoLabels[selectedEvento.estado] || selectedEvento.estado}
                </span>
                {(selectedEvento.estado === 'cancelado' || selectedEvento.estado === 'aplazado') && selectedEvento.motivoEstado && (
                  <span className="text-xs text-gray-500">Motivo: {selectedEvento.motivoEstado}</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Fecha</p>
                  <p className="text-xs font-medium text-gray-900">{formatDate(selectedEvento.fecha)}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Lugar</p>
                  <p className="text-xs font-medium text-gray-900">{selectedEvento.lugar || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Hora inicio</p>
                  <p className="text-xs font-medium text-gray-900">{selectedEvento.horaInicio || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Hora fin</p>
                  <p className="text-xs font-medium text-gray-900">{selectedEvento.horaFin || '-'}</p>
                </div>
              </div>

              {/* Costos */}
              {(selectedEvento.costoPorPersona != null || selectedEvento.presupuesto != null) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedEvento.costoPorPersona != null && (
                    <div className="bg-purple-50 rounded-md p-3 flex items-center gap-3">
                      <DollarSign className="w-4 h-4 text-purple-600" />
                      <div>
                        <p className="text-xs text-purple-600">Costo por persona</p>
                        <p className="text-xs font-medium text-purple-900">{formatCurrency(selectedEvento.costoPorPersona)}</p>
                      </div>
                    </div>
                  )}
                  {selectedEvento.presupuesto != null && (
                    <div className="bg-blue-50 rounded-md p-3 flex items-center gap-3">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-blue-600">Presupuesto estimado</p>
                        <p className="text-xs font-medium text-blue-900">{formatCurrency(selectedEvento.presupuesto)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedEvento.notasPresupuesto && (
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Notas de presupuesto</p>
                  <p className="text-xs text-gray-900 whitespace-pre-line">{selectedEvento.notasPresupuesto}</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-xs text-gray-500">Descripción</p>
                <p className="text-xs text-gray-900 whitespace-pre-line">
                  {selectedEvento.descripcion || 'Sin descripción'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-xs text-gray-500">Ministerio</p>
                <p className="text-xs font-medium text-gray-900">{selectedEvento.ministerioNombres}</p>
              </div>

              {/* Inscripción */}
              {selectedEvento.requiereInscripcion && (
                <div className="bg-orange-50 border border-orange-100 rounded-md p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-600" />
                    <div>
                      <p className="text-xs text-orange-600">Requiere inscripción</p>
                      {selectedEvento.capacidadMaxima != null && (
                        <p className="text-xs text-orange-700">Capacidad máxima: {selectedEvento.capacidadMaxima} personas</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setInscripcionesEventoId(selectedEvento.id)}
                    className="text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1"
                  >
                    <CalendarCheck className="w-3 h-3" />
                    Ver inscripciones
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drawer de inscripciones */}
      <EventoInscripcionesDrawer
        eventoId={inscripcionesEventoId}
        onClose={() => setInscripcionesEventoId(null)}
      />
    </div>
  );
};

export default DashboardEventosPage;
